import React, { Suspense } from 'react';
import { useCompany } from 'src/app/Company.store';
import { readCachedBrandColor } from 'src/app/brandColorCache';
import { FEEDBACK_COOKIE } from '../../../feedback.config.mjs';

// Brand-navy fallback read from the --brand-navy token (src/ui-new/tokens.css)
// instead of hardcoding the hex — keeps a single source of truth if the brand
// palette changes. Read lazily so it runs after CSS has loaded; only reached for
// internal staff, and only when neither the live config nor the cached tenant
// color is available.
const readBrandNavyToken = (): string | undefined => {
  if (typeof window === 'undefined') return undefined;
  return (
    getComputedStyle(document.documentElement)
      .getPropertyValue('--brand-navy')
      .trim() || undefined
  );
};

// Lazy: react-linear-feedback pulls in modern-screenshot (~85KB pre-min). The
// widget is internal-only and renders nothing for ordinary visitors, so keep it
// out of the entry chunk and import it only when the gate is actually active.
const LazyFeedbackGate = React.lazy(() =>
  import('react-linear-feedback/react').then((m) => ({
    default: m.FeedbackGate,
  }))
);

// True when the feedback gate is (or is about to be) active: the gate cookie
// from a prior `?feedback` visit, or the `?feedback` param on this load (the
// gate sets the cookie on mount). Anything else is an ordinary visitor, so we
// skip the dynamic import entirely.
const isFeedbackActive = () =>
  typeof document !== 'undefined' &&
  (document.cookie.includes(`${FEEDBACK_COOKIE}=1`) ||
    new URLSearchParams(window.location.search).has('feedback'));

/**
 * App-wide feedback button that files Linear issues via /api/feedback.
 *
 * Internal-only: <FeedbackGate> renders nothing until the portal is opened with
 * `?feedback`, which sets the gate cookie (90 days, the gate default). The
 * server function (api/feedback.mjs) and the local dev middleware both reject
 * submissions lacking that cookie. The cookie name is shared via
 * feedback.config.mjs (FEEDBACK_COOKIE) so client and both servers can't drift.
 * Mounted inside <CompanyStoreProvider> so useCompany() resolves the tenant
 * brand color.
 */
export const FeedbackWidget = () => {
  const { data: company } = useCompany();

  // Ordinary visitor: render nothing and never pull in the widget chunk.
  if (!isFeedbackActive()) return null;

  const brandColor =
    company?.primary_color || readCachedBrandColor() || readBrandNavyToken();

  return (
    <Suspense fallback={null}>
      {/* `position="right"` renders the compact icon-only edge tab (needs
          react-linear-feedback >= 0.2.0). */}
      <LazyFeedbackGate
        endpoint="/api/feedback"
        cookieName={FEEDBACK_COOKIE}
        brandColor={brandColor}
        position="right"
      />
    </Suspense>
  );
};
